import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  TextField,
  InputAdornment,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
  Typography,
  Stack,
  Link,
  Collapse,
} from '@mui/material';
import {
  Search as SearchIcon,
  KeyboardArrowRight as ExpandRowIcon,
  KeyboardArrowDown as CollapseRowIcon,
  Description as ViewIcon,
  LocalShipping as TrackIcon,
  Print as PrintIcon,
  CheckCircle as DeliveredIcon,
  DriveEta as InTransitIcon,
  History as HistoryIcon,
  LocalShipping as DeliveryIcon,
  Schedule as ScheduleIcon,
  SwapHoriz as SwapIcon,
  AccessTime as EtaIcon,
  OpenInNew as TrackingLinkIcon,
  RecordVoiceOver as CounselIcon,
  CallSplit as SplitIcon,
  WarningAmber as WarningIcon,
  Link as VisitLinkIcon,
  CameraAlt as CameraAltIcon,
} from '@mui/icons-material';
import { Button } from '@mui/material';
import LabelPrintingModal from '../../printing/LabelPrintingModal';
import PastOrdersDialog from '../dialogs/PastOrdersDialog';
import type { HardcopySignature, SignatureType } from '../../common/HardcopyViewer';
import api from '../../../services/api';
import { SwapRxDialog } from '../../orders/SwapRxDialog';
import { getActiveOrderDrugs, getInactiveRxNumbers } from '../overview/orderDrugLinking';
import { findDuplicateRxOrders } from './duplicateRxOrders';
import { getSplitProvenance, formatSplitOrigin } from './splitProvenance';
import { getUserDisplayName, useBatchUserLookup } from '../../../hooks/useUserLookup';
import { getTrackingUrl } from '../../order-fulfillment/orderQueueColumns';
import { getPackagedLotExpiryForRx } from '../../../utils/packagingVerificationHelpers';
import { deriveOrderCompletionState } from '../../../utils/orderCompletionState';
import { OrderVerificationPhotosRow } from './OrderVerificationPhotosRow';
import type { VerificationPhoto } from '../../../services/verificationPhotoService';

interface OrdersTableProps {
  orders: any[];
  loading?: boolean;
  onViewDetails?: (order: any) => void;
  onTrackOrder?: (order: any) => void;
  pharmacyId?: string;
  patientId?: string;
  patientName?: string;
  /** When true, disables navigation links and calls onOrderSelect instead */
  embedded?: boolean;
  /** Callback when an order is selected in embedded mode */
  onOrderSelect?: (order: any) => void;
  /** Callback to refresh orders data (e.g., after RX swap) */
  onRefresh?: () => void;
  /**
   * GP-1222: when provided, each order row shows an "Add Counseling Task" action
   * that hands the order back to the caller (Patient 360 Order History), which
   * opens the Create New Task dialog pre-filled from it. Optional — surfaces that
   * don't pass it (embedded order pickers, past-orders dialog) show no such action.
   */
  onAddCounselingTask?: (order: any) => void;
  /**
   * Per-Rx current subscription follow-up/due date, keyed by rxNumber. When
   * provided, expanding an order row shows each drug's next follow-up (sourced
   * from the live subscription — matches the Overview tab). Optional: surfaces
   * that don't pass it simply show "—" in that column.
   */
  rxDueDates?: Record<string, string | null>;
}

type OrderBy = 'orderNumber' | 'patientName' | 'medicationName' | 'createdAt' | 'deliveryDate' | 'status' | 'workflowStage';

// GP-1379 (batch-5 review): the route + tab logic moved to a neutral util so
// orderQueueColumns' Status/Stage chip (the second copy of this routing, used
// by All Orders) can share it — it cannot import from this file, which already
// depends on orderQueueColumns. Re-exported here so existing imports hold.
import { DELIVERY_STATUS_ROUTE, deliveryStatusTabFor } from '../../../utils/deliveryStatusRoute';
import { pickupStageLabel } from '../../../utils/pickupStageLabel';
export { DELIVERY_STATUS_ROUTE, deliveryStatusTabFor };

export const OrdersTable: React.FC<OrdersTableProps> = ({
  orders = [],
  loading = false,
  onViewDetails,
  patientId,
  patientName,
  onTrackOrder,
  pharmacyId,
  embedded = false,
  onOrderSelect,
  onRefresh,
  rxDueDates,
  onAddCounselingTask,
}) => {
  const navigate = useNavigate();
  const [expandedOrders, setExpandedOrders] = useState<Set<string>>(new Set());
  const toggleExpanded = (orderId: string) => {
    setExpandedOrders((prev) => {
      const next = new Set(prev);
      next.has(orderId) ? next.delete(orderId) : next.add(orderId);
      return next;
    });
  };
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [workflowStageFilter, setWorkflowStageFilter] = useState<string>('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState<OrderBy>('createdAt');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [printModalOpen, setPrintModalOpen] = useState(false);
  const [selectedOrderForPrint, setSelectedOrderForPrint] = useState<any>(null);
  const [signatures, setSignatures] = useState<Record<string, HardcopySignature[]>>({});
  // Merge-train 2026-08-27 T4 fix: a failed or still-pending signature fetch
  // must not read as "this order shipped unverified" — the red
  // missing-after-dispatch state may only be claimed from a successful
  // response, so an outage degrades to the old neutral grey, never to a
  // false defect claim.
  const [signaturesLoaded, setSignaturesLoaded] = useState(false);
  const [orderHistoryOpen, setOrderHistoryOpen] = useState(false);
  const [swapRxDialogOpen, setSwapRxDialogOpen] = useState(false);
  const [selectedOrderForSwap, setSelectedOrderForSwap] = useState<any>(null);

  // Filter and sort data
  const filteredAndSortedData = useMemo(() => {
    let filtered = orders;

    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (ord) =>
          ord.orderNumber?.toLowerCase().includes(search) ||
          ord.patientName?.toLowerCase().includes(search) ||
          ord.medicationName?.toLowerCase().includes(search) ||
          ord.rxNumber?.toLowerCase().includes(search)
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter((ord) => ord.status === statusFilter);
    }

    if (workflowStageFilter !== 'all') {
      filtered = filtered.filter((ord) => ord.workflowStage === workflowStageFilter);
    }

    const sorted = [...filtered].sort((a, b) => {
      let aValue = a[orderBy];
      let bValue = b[orderBy];

      if (orderBy === 'createdAt' || orderBy === 'deliveryDate') {
        aValue = aValue ? new Date(aValue).getTime() : 0;
        bValue = bValue ? new Date(bValue).getTime() : 0;
      }

      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue?.toLowerCase() || '';
      }

      if (aValue < bValue) return order === 'asc' ? -1 : 1;
      if (aValue > bValue) return order === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [orders, searchTerm, statusFilter, workflowStageFilter, orderBy, order]);

  const paginatedData = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredAndSortedData.slice(start, start + rowsPerPage);
  }, [filteredAndSortedData, page, rowsPerPage]);

  // GP-1226: A split order (a second order added to the SAME patient/delivery)
  // produces two rows that share one base order number — a "TS-ORD-…" (or cold-chain
  // "CC-TS-ORD-…") half and a plain "ORD-…" half (e.g. TS-ORD-20260717-203255-431 and
  // ORD-20260717-203255-431). Previously they rendered as two unrelated rows in
  // conflicting stages ("Packaging" vs "Ready") with nothing linking them, so a
  // verifier could clear one and strand its paired item. Group the full order set by
  // shared base number to recover the split relationship. Grouping runs over the whole
  // `orders` array (not the current page) so a sibling on another page still links.
  const getBaseOrderNumber = (orderNumber?: string): string =>
    String(orderNumber || '').replace(/^(CC-)?(TS-)?/, '');

  const splitSiblingsById = useMemo(() => {
    const byBase = new Map<string, any[]>();
    for (const ord of orders) {
      if (!ord?.orderNumber || !ord?.id) continue;
      const base = getBaseOrderNumber(ord.orderNumber);
      const list = byBase.get(base) || [];
      list.push(ord);
      byBase.set(base, list);
    }
    const result = new Map<string, any[]>();
    for (const group of byBase.values()) {
      // A genuine split needs 2+ DISTINCT order ids sharing the base number — a lone
      // order (or the same order seen twice) is not a split.
      const distinct = Array.from(new Map(group.map((o) => [o.id, o])).values());
      if (distinct.length < 2) continue;
      for (const ord of distinct) {
        result.set(ord.id, distinct.filter((o) => o.id !== ord.id));
      }
    }
    return result;
  }, [orders]);

  // GP-1391: two UNRELATED orders carrying the same Rx number are a duplicate
  // fill — the patient is queued to receive and be billed for one prescription
  // twice (prod: Rx 1483723 on one order at Product
  // Verification and another at Ready to Ship, $65.01 pending on each). The two
  // rows were visually identical apart from the order number, so the collision
  // was only findable by opening both detail views and comparing hardcopies.
  // Computed over the whole `orders` array (not the current page) so a
  // duplicate on another page still flags. Cancelled orders and BO-/CC-/TS-
  // children of the same order are excluded — see duplicateRxOrders.ts.
  const duplicateRxById = useMemo(() => findDuplicateRxOrders(orders), [orders]);

  // GP-1431: a split row must say which order it was carved out of, and why —
  // the amber "Split · N items" chip above only says "these ship together".
  // the reported patient's three rows (ORD-…-603 / TS-ORD-…-603-1786654030834 /
  // CC-TS-ORD-…-603-1786654030834) forced the reporter into both detail views and
  // a 20-event timeline just to learn that the cold-chain child came off the TS
  // order. Read the recorded provenance instead — see splitProvenance.ts.
  const splitProvenanceById = useMemo(() => {
    const result = new Map<string, ReturnType<typeof getSplitProvenance>>();
    for (const ord of orders) {
      if (!ord?.id) continue;
      const provenance = getSplitProvenance(ord);
      if (provenance) result.set(ord.id, provenance);
    }
    return result;
  }, [orders]);

  // The parent order may be on another page of this table — index the whole
  // `orders` array so the row can link to it by id, and fall back to the recorded
  // parent number when the parent is not in the patient's loaded set at all.
  const orderIdsByNumber = useMemo(() => {
    const map = new Map<string, any>();
    for (const ord of orders) {
      if (ord?.orderNumber && ord?.id) map.set(String(ord.orderNumber), ord);
    }
    return map;
  }, [orders]);

  // `splitBy` / `removedBy` are stored as Auth0 subjects. Resolve them to display
  // names (batched + cached) so the row reads "Sherif Elderby" and not `auth0|…`.
  const splitActorIds = useMemo(
    () => Array.from(splitProvenanceById.values()).map((p) => p?.splitByUserId ?? undefined),
    [splitProvenanceById],
  );
  const { userNames: splitActorNames } = useBatchUserLookup(splitActorIds);

  // Fetch signatures for all orders
  React.useEffect(() => {
    const fetchSignatures = async () => {
      if (!pharmacyId || orders.length === 0) return;

      const rxNumbers: string[] = [];
      orders.forEach(ord => {
        // Add top-level rxNumber
        if (ord.rxNumber) rxNumbers.push(String(ord.rxNumber));
        // Add rxNumbers from order items (multi-Rx orders)
        if (ord.items && Array.isArray(ord.items)) {
          ord.items.forEach((item: any) => {
            if (item.rxNumber) rxNumbers.push(String(item.rxNumber));
          });
        }
        // GP-1131 (Codex review): also request the recovered/active Rx set —
        // for corrupted multi-fill orders the true per-fill Rx live only in
        // billingData.medications, and without this their signatures were
        // never fetched, so the verification triad had nothing to count.
        getActiveOrderDrugs(ord).rxNumbers.forEach((rx: string) => rxNumbers.push(String(rx)));
      });
      const uniqueRxNumbers = rxNumbers.filter((rxNum, index, self) => self.indexOf(rxNum) === index);

      if (uniqueRxNumbers.length === 0) return;

      try {
        const response = await api.get(
          `/api/rx/${pharmacyId}/signatures-batch?rxNumbers=${encodeURIComponent(uniqueRxNumbers.join(','))}`
        );

        if (response.data?.success) {
          const fetchedSignatures = response.data.data?.signatures || [];
          const signaturesByRx: Record<string, HardcopySignature[]> = {};
          fetchedSignatures.forEach((sig: HardcopySignature) => {
            const rxNum = String(sig.rxNumber);
            if (!signaturesByRx[rxNum]) {
              signaturesByRx[rxNum] = [];
            }
            signaturesByRx[rxNum].push(sig);
          });
          setSignatures(signaturesByRx);
          setSignaturesLoaded(true);
        }
      } catch (error) {
        console.error('Failed to fetch signatures:', error);
        setSignaturesLoaded(false);
      }
    };

    fetchSignatures();
  }, [pharmacyId, orders]);

  const handleSort = (property: OrderBy) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const getWorkflowStageColor = (stage: string): 'primary' | 'secondary' | 'success' | 'warning' | 'info' | 'error' | 'default' => {
    switch (stage) {
      case 'clinical_verification':
        return 'warning';
      case 'packaging':
        return 'info';
      case 'product_verification':
        return 'primary';
      case 'ready_to_ship':
        return 'secondary';
      case 'staging':
        return 'info';
      case 'out_for_delivery':
        return 'primary';
      case 'delivered':
        return 'success';
      default:
        return 'default';
    }
  };

  const getPriorityColor = (priority: string): 'error' | 'warning' | 'info' | 'default' => {
    switch (priority?.toLowerCase()) {
      case 'critical':
      case 'urgent':
        return 'error';
      case 'high':
        return 'warning';
      case 'routine':
        return 'info';
      default:
        return 'default';
    }
  };

  const formatWorkflowStage = (stage: string): string => {
    if (!stage) return '-';
    return stage
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // GP-249: Show troubleshooting source stage for T/S orders
  const getStageDisplayName = (stage: string, order?: any): string => {
    // If in troubleshooting, show the source stage + "T/S"
    if (order?.inTroubleshooting && order?.troubleshootingSourceStage) {
      const sourceDisplayMap: Record<string, string> = {
        'billing': 'Billing T/S',
        'clinic_scheduling': 'Scheduling T/S',
        'clinical_verification': 'Clinical T/S',
        'packaging': 'Packaging T/S',
        'product_verification': 'Product T/S',
        'ready_to_ship': 'Ready T/S',
        'staging': 'Staging T/S',
      };
      return sourceDisplayMap[order.troubleshootingSourceStage] || 'Troubleshooting';
    }
    // GP-1405: this chip read "Ready" on a row whose own Delivery cell said
    // "pickup" — the stage map never looked at the fulfilment method. Checked
    // after the T/S branch above: "Ready T/S" outranks it and claims no truck.
    const pickupLabel = pickupStageLabel(stage, order?.deliveryMethod ?? order?.orderType);
    if (pickupLabel) return pickupLabel;
    const displayMap: Record<string, string> = {
      'clinic_scheduling': 'Scheduling',
      'clinical_verification': 'Clinical',
      'product_verification': 'Product',
      'ready_to_ship': 'Ready',
      'out_for_delivery': 'Out',
    };
    return displayMap[stage] || formatWorkflowStage(stage);
  };

  // GP-249: Route to correct queue, including troubleshooting queues
  const getStageRoute = (workflowStage: string, order?: any): string => {
    // If in troubleshooting, route to the troubleshooting tab of the source stage queue
    if (order?.inTroubleshooting && order?.troubleshootingSourceStage) {
      const sourceStage = order.troubleshootingSourceStage;
      // Billing T/S goes to billing queue troubleshooting tab
      if (sourceStage === 'billing') return '/order-fulfillment/billing-queue';
      // Scheduling T/S goes to schedule delivery queue
      if (sourceStage === 'clinic_scheduling') return '/order-fulfillment/schedule-delivery';
      // Other T/S — fall through to main stage route
    }
    const stageRouteMap: Record<string, string> = {
      billing: '/order-fulfillment/billing-queue',
      clinical_verification: '/order-fulfillment/clinical-verification',
      packaging: '/order-fulfillment/packaging',
      product_verification: '/order-fulfillment/product-verification',
      ready_to_ship: '/order-fulfillment/ready-to-ship',
      staging: '/order-fulfillment/staging-area',
      out_for_delivery: DELIVERY_STATUS_ROUTE,
      // GP-1379: the Delivery Status queue's Pickup tab is where a
      // ready-for-pickup order is actually worked. Unmapped, it fell through to
      // the default below and sent the operator to the Billing Queue.
      ready_for_pickup: DELIVERY_STATUS_ROUTE,
      // Batch-5 review: delivery_missed is worked from the same queue — it was
      // the one stage this fix newly surfaces that still deep-linked to Billing.
      delivery_missed: DELIVERY_STATUS_ROUTE,
      clinic_scheduling: '/order-fulfillment/schedule-delivery',
    };
    return stageRouteMap[workflowStage] || '/order-fulfillment/billing-queue';
  };

  const formatDate = (dateValue: any): string => {
    if (!dateValue) return '-';
    try {
      const dateStr = String(dateValue);
      const datePart = dateStr.split('T')[0];
      const [year, month, day] = datePart.split('-');
      if (!year || !month || !day) return '-';
      return `${day}/${month}/${year}`;
    } catch (error) {
      return '-';
    }
  };

  // GP-99: Format date with day of week (e.g., "Wed, Jan 22, 2026")
  const formatDateWithDay = (dateValue: any): string => {
    if (!dateValue) return '-';
    try {
      const dateStr = String(dateValue);
      const datePart = dateStr.split('T')[0];
      const [year, month, day] = datePart.split('-').map(Number);
      if (!year || !month || !day) return '-';
      const date = new Date(year, month - 1, day);
      const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${dayNames[date.getDay()]}, ${monthNames[date.getMonth()]} ${day}, ${year}`;
    } catch (error) {
      return '-';
    }
  };

  // GP-1431: a split's `splitAt` / `removedAt` is a real ISO INSTANT (not a
  // date-only field), so converting it to the viewer's calendar day is correct
  // here — unlike `deliveryDate`, which must never go through a UTC conversion.
  const formatSplitTimestamp = (iso?: string | null): string | null => {
    if (!iso) return null;
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return null;
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    return `${dd}/${mm}/${date.getFullYear()}`;
  };

  // GP-99: Format delivery address from snapshot
  const formatAddress = (ord: any): string | null => {
    const addr = ord.deliveryAddressSnapshot;
    if (!addr?.street) return null;
    const parts = [addr.street];
    if (addr.unit) parts[0] += ` ${addr.unit}`;
    if (addr.city) parts.push(addr.city);
    return parts.join(', ');
  };

  // Get prescriber from metadata
  const getPrescriber = (ord: any): string => {
    return ord.metadata?.prescriberName || ord.metadata?.prescriber || '-';
  };

  // Distinct Rx numbers on an order (the denominator for per-check completion).
  // GP-1131 (Codex review): route through getActiveOrderDrugs so this uses the
  // SAME recovered/active Rx set the Rx and Medication cells render (corruption-
  // signature switch + inactive filtering) — otherwise a corrupted multi-fill
  // order displays 8 prescriptions while the D/C/P triad counts 1.
  const getOrderRxNumbers = (ord: any): string[] => {
    // No extra ord.rxNumber fallback (Sentry review on #1815): the util already
    // falls back to the order-level rxNumber itself — comma-split AND filtered
    // through the inactive set — so re-adding it here would resurrect an
    // inactive Rx into the D/C/P denominator when every drug was removed.
    const { rxNumbers } = getActiveOrderDrugs(ord);
    return Array.from(new Set(rxNumbers.map(String)));
  };

  // Get all signatures for an order (including all Rx numbers from items)
  const getOrderSignatures = (ord: any): HardcopySignature[] => {
    const allSigs: HardcopySignature[] = [];
    getOrderRxNumbers(ord).forEach(rxNum => {
      if (signatures[rxNum]) allSigs.push(...signatures[rxNum]);
    });
    return allSigs;
  };

  // GP-1008: the product verifier's name for ONE Rx's receipt reprint. The original
  // PV receipt printed the verifying user's full name (GP-654); the durable record
  // of who signed is that Rx's `product_verification` signature (its `userName`).
  // Using it keeps the reprinted receipt's signature line identical to the original
  // instead of blank.
  //
  // Resolved per Rx, never per order: on a multi-Rx order each drug can be verified
  // by a different person, and stamping one drug's verifier onto another drug's
  // receipt is a wrong signature, not a fallback. Returns undefined when this Rx has
  // no product-verification signature — never a fabricated name.
  const getReprintProductVerifierNameForRx = (rxNumber: unknown): string | undefined => {
    const rx = String(rxNumber ?? '').trim();
    if (!rx) return undefined;
    const sig = (signatures[rx] || []).find((s) => s.signatureType === 'product_verification');
    return sig ? sig.userName || sig.initials || undefined : undefined;
  };

  // GP-1008/GP-1322: reprint parity — hang each medication's OWN packaged
  // lot/expiry and product verifier off the medication, so the print modal
  // reproduces per drug what packaging/PV originally printed.
  // `metadata.packagingData` is a per-Rx COLLECTION (merged by rxNumber as each
  // drug is packaged), which is why the previous order-level read of
  // `packagingData.lotNumber` resolved to undefined and reprinted a blank
  // expiry. `getPackagedLotExpiryForRx` only borrows a sole record when that
  // record carries NO rxNumber of its own — on a partially-packaged multi-Rx
  // order it never hands one drug's lot/expiry to another drug's label.
  const withReprintParity = (ord: any, meds: any[]): any[] =>
    meds.map((med) => ({
      ...med,
      ...(getPackagedLotExpiryForRx(ord?.metadata?.packagingData, med?.rxNumber) || {}),
      productVerifierName: getReprintProductVerifierNameForRx(med?.rxNumber),
    }));

  // Verification triad: collapse per-Rx signatures into Data / Clinical / Product
  // completion so the column shows three meaningful badges instead of one green
  // chip per signature.
  const VERIFICATION_CHECKS: Array<{ type: SignatureType; short: string; label: string }> = [
    { type: 'data_verification', short: 'D', label: 'Data' },
    { type: 'clinical_verification', short: 'C', label: 'Clinical' },
    { type: 'product_verification', short: 'P', label: 'Product' },
  ];

  /**
   * GP-1436 — stages an order can only occupy AFTER the fill left the bench.
   * A missing check before this point is just work not done yet; at or past it
   * the drug is on a truck, on the pickup shelf, or already with the patient,
   * and the check is genuinely missing.
   */
  const DISPATCHED_STAGES: readonly string[] = [
    'staging',
    'out_for_delivery',
    'delivery_missed',
    'delivered',
    'ready_for_pickup',
    'picked_up',
  ];

  const hasLeftTheBench = (ord: any): boolean =>
    DISPATCHED_STAGES.includes(String(ord.workflowStage || '').toLowerCase());

  const getVerificationSummary = (ord: any) => {
    const total = getOrderRxNumbers(ord).length;
    const sigs = getOrderSignatures(ord);
    // GP-1436: TS-TS-ORD-20260721-153907-168 (Tresiba) sat in this
    // table reading Delivered with three blank grey chips, one row under the
    // Admelog from the same fill showing three green ones. The patient had only
    // ever received the Admelog. Grey is the same colour this column uses for
    // "not its turn yet", so the difference between the two rows read as noise.
    // Once the order has left the bench a missing check is a defect, not a gap.
    const dispatched =
      signaturesLoaded && hasLeftTheBench(ord) && ord.status !== 'cancelled' && !ord.toBeCancelled;
    return VERIFICATION_CHECKS.map((check) => {
      const matching = sigs.filter((s) => s.signatureType === check.type);
      const count = new Set(matching.map((s) => String(s.rxNumber))).size;
      const signers = Array.from(new Set(matching.map((s) => s.userName).filter(Boolean)));
      const state: 'complete' | 'partial' | 'none' =
        total > 0 && count >= total ? 'complete' : count > 0 ? 'partial' : 'none';
      return { ...check, count, total, state, signers, missingAfterDispatch: dispatched && state !== 'complete' };
    });
  };

  const isActiveOrder = (ord: any): boolean => {
    const s = String(ord.status || '').toLowerCase();
    // GP-1338: a `completed` row that has since come BACK into the workflow is
    // active work again, whatever the stored status says.
    if (deriveOrderCompletionState(ord).isReopenedAfterCompletion) return !ord.toBeCancelled;
    return s !== 'completed' && s !== 'cancelled' && s !== 'failed' && !ord.toBeCancelled;
  };

  // One meaningful status pill per order: the fulfilment OUTCOME for terminal
  // orders (Delivered / Picked Up / Cancelled), or the current STAGE (clickable)
  // for active ones. Green is reserved for genuinely-good outcomes.
  const getPrimaryStatus = (
    ord: any,
  ): {
    label: string;
    color: 'success' | 'error' | 'warning' | 'info' | 'primary' | 'secondary' | 'default';
    variant: 'filled' | 'outlined';
    clickable: boolean;
    tooltip?: string;
  } => {
    if (ord.toBeCancelled) return { label: 'Cancel Pending', color: 'warning', variant: 'filled', clickable: false };
    const status = String(ord.status || '').toLowerCase();
    if (status === 'cancelled') return { label: 'Cancelled', color: 'default', variant: 'outlined', clickable: false };
    if (status === 'failed') return { label: 'Failed', color: 'error', variant: 'outlined', clickable: false };
    // GP-1338: only paint the terminal outcome when the stored `completed` status
    // is still true. A row that was closed and then rescheduled / pulled back into
    // troubleshooting keeps `status='completed'` while `workflowStage` moves on —
    // it falls through to the live-stage pill below so this grid says the same
    // thing the queue it is sitting in says.
    if (status === 'completed' && !deriveOrderCompletionState(ord).isReopenedAfterCompletion) {
      if (ord.workflowStage === 'picked_up') return { label: 'Picked Up', color: 'success', variant: 'filled', clickable: false };
      if (ord.workflowStage === 'delivered') return { label: 'Delivered', color: 'success', variant: 'filled', clickable: false };
      // Not the same thing as Delivered — the fulfilment lifecycle is closed but no
      // delivery or pickup was ever recorded. Outlined + explained, so the two green
      // chips are no longer visually interchangeable.
      return {
        label: 'Completed',
        color: 'success',
        variant: 'outlined',
        clickable: false,
        tooltip: 'Fulfillment lifecycle closed — no delivery or pickup was recorded on this order.',
      };
    }
    // Active (pending / in_progress), or completed-then-reopened: the pill IS the
    // current stage.
    if (ord.workflowStage) {
      const inTS = ord.inTroubleshooting && ord.workflowStage !== 'cancelled';
      return {
        label: getStageDisplayName(ord.workflowStage, ord),
        color: inTS ? 'error' : getWorkflowStageColor(ord.workflowStage),
        variant: 'filled',
        clickable: true,
      };
    }
    return { label: ord.status || '-', color: 'default', variant: 'outlined', clickable: false };
  };

  // Per-drug detail for the expandable row: name, strength, quantity, days supply,
  // and (when rxDueDates is supplied) the drug's current subscription follow-up.
  const getOrderDrugDetails = (
    ord: any,
  ): Array<{
    rxNumber: string;
    name: string;
    strength: string;
    quantity: any;
    daysSupply: any;
    nextFollowUp: string | null;
    isPrn: boolean;
    din: string;
    upc: string;
    checks: Array<{ short: string; label: string; done: boolean }>;
  }> => {
    const { medications } = getActiveOrderDrugs(ord);
    const billingMeds: any[] = Array.isArray(ord?.metadata?.billingData?.medications)
      ? ord.metadata.billingData.medications
      : [];
    const byRx = new Map(billingMeds.map((m: any) => [String(m.rxNumber), m]));
    const list = medications && medications.length ? medications : [];
    return list.map((d: any) => {
      const rx = String(d?.rxNumber ?? '');
      const b: any = byRx.get(rx) || {};
      const rxSigs = signatures[rx] || [];
      return {
        rxNumber: rx,
        // Prefer the clean billing name + separate strength; fall back to the
        // item's productName (which embeds the strength) when no billing entry.
        name: b.medicationName || d.medicationName || d.productName || '-',
        strength: b.strength || d.strength || '',
        quantity: d.quantity ?? b.quantity,
        daysSupply: d.daysSupply ?? b.daysSupply,
        nextFollowUp: rxDueDates ? (rxDueDates[rx] ?? null) : null,
        // PRN drugs intentionally have no scheduled refill — flag them so a blank
        // follow-up reads as "as needed", not a missed gap.
        isPrn: !!(d?.metadata?.billingData?.isPrn || d?.isPrn || b?.isPrn),
        din: d.productDin || b.din || '',
        upc: d.productUpc || b.upc || '',
        // Per-drug verification (Data / Clinical / Product) for THIS Rx.
        checks: VERIFICATION_CHECKS.map((c) => ({
          short: c.short,
          label: c.label,
          done: rxSigs.some((s) => s.signatureType === c.type),
        })),
      };
    });
  };

  // Print a single drug's label by scoping the existing LabelPrintingModal to one med.
  const handlePrintDrug = (
    ord: any,
    drug: { rxNumber: string; name: string; strength: string; quantity: any; din: string; upc: string },
  ) => {
    setSelectedOrderForPrint({
      // GP-1234: keep the order's own fields (deliveryMethod, deliveryType,
      // deliveryAddressSnapshot, deliveryDate) — this path used to hand the
      // modal a bare 3-field object, so a single-drug reprint lost the address
      // and the fulfillment type the row-level Print action carries.
      ...ord,
      id: ord.id,
      orderNumber: ord.orderNumber,
      patientName: ord.patientName,
      // GP-1008: carry the order metadata + delivery snapshot so the reprint can
      // reuse the packaged lot/expiry and address the original print used.
      metadata: ord.metadata,
      deliveryAddressSnapshot: ord.deliveryAddressSnapshot,
      deliveryType: ord.deliveryType,
      deliveryDate: ord.deliveryDate,
      medications: [
        {
          rxNumber: drug.rxNumber,
          medication: drug.name,
          strength: drug.strength,
          quantity: drug.quantity != null ? String(drug.quantity) : '',
          directions: '',
          prescriber: getPrescriber(ord),
          din: drug.din || '',
          upc: drug.upc || '',
        },
      ],
    });
    setPrintModalOpen(true);
  };

  return (
    <Box>
      {/* Filters */}
      <Paper sx={{ p: 1.5, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center">
          <TextField
            placeholder="Search order #, patient, medication, RX#..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(0);
            }}
            size="small"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ maxWidth: 350 }}
          />
          <TextField
            select
            label="Status"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(0);
            }}
            size="small"
            sx={{ minWidth: 130 }}
          >
            <MenuItem value="all">All Status</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="in_progress">In Progress</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
            <MenuItem value="cancelled">Cancelled</MenuItem>
            <MenuItem value="delivered">Delivered</MenuItem>
          </TextField>
          <TextField
            select
            label="Stage"
            value={workflowStageFilter}
            onChange={(e) => {
              setWorkflowStageFilter(e.target.value);
              setPage(0);
            }}
            size="small"
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="all">All Stages</MenuItem>
            <MenuItem value="clinical_verification">Clinical</MenuItem>
            <MenuItem value="packaging">Packaging</MenuItem>
            <MenuItem value="product_verification">Product</MenuItem>
            <MenuItem value="ready_to_ship">Ready to Ship</MenuItem>
            <MenuItem value="staging">Staging</MenuItem>
            <MenuItem value="out_for_delivery">Out for Delivery</MenuItem>
            <MenuItem value="delivered">Delivered</MenuItem>
          </TextField>

          {patientId && patientName && (
            <Button
              variant="outlined"
              startIcon={<HistoryIcon />}
              onClick={() => setOrderHistoryOpen(true)}
              size="small"
            >
              History
            </Button>
          )}
        </Stack>
      </Paper>

      {/* Compact Table */}
      <TableContainer component={Paper}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow sx={{ '& th': { bgcolor: 'grey.100', fontWeight: 600, fontSize: '0.75rem', py: 1 } }}>
              {/* Order Info: Order # + Created */}
              <TableCell sx={{ width: 140 }}>
                <TableSortLabel
                  active={orderBy === 'orderNumber'}
                  direction={orderBy === 'orderNumber' ? order : 'asc'}
                  onClick={() => handleSort('orderNumber')}
                >
                  Order / Created
                </TableSortLabel>
              </TableCell>

              {/* Rx — patient name omitted: this table is always scoped to a single
                  patient (Patient 360 / patient dialogs), so the name is redundant. */}
              <TableCell sx={{ minWidth: 100 }}>Rx</TableCell>

              {/* Medication / Prescriber */}
              <TableCell sx={{ minWidth: 160 }}>
                <TableSortLabel
                  active={orderBy === 'medicationName'}
                  direction={orderBy === 'medicationName' ? order : 'asc'}
                  onClick={() => handleSort('medicationName')}
                >
                  Medication
                </TableSortLabel>
              </TableCell>

              {/* Delivery Info: Date + Delivered + Method */}
              <TableCell sx={{ width: 140 }}>
                <TableSortLabel
                  active={orderBy === 'deliveryDate'}
                  direction={orderBy === 'deliveryDate' ? order : 'asc'}
                  onClick={() => handleSort('deliveryDate')}
                >
                  Delivery
                </TableSortLabel>
              </TableCell>

              {/* Status / Stage / Priority */}
              <TableCell sx={{ width: 150 }}>
                <TableSortLabel
                  active={orderBy === 'status'}
                  direction={orderBy === 'status' ? order : 'asc'}
                  onClick={() => handleSort('status')}
                >
                  Status / Stage
                </TableSortLabel>
              </TableCell>

              {/* Payment */}
              <TableCell sx={{ width: 90 }} align="center">Payment</TableCell>

              {/* Signatures */}
              <TableCell sx={{ width: 80 }} align="center">Checks</TableCell>

              {/* Actions */}
              <TableCell sx={{ width: 90 }} align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">Loading...</Typography>
                </TableCell>
              </TableRow>
            ) : paginatedData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    {searchTerm || statusFilter !== 'all' || workflowStageFilter !== 'all'
                      ? 'No orders match your filters'
                      : 'No orders found'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedData.map((ord) => {
                const isCancelled = ord.status === 'cancelled';
                return (
                  <React.Fragment key={ord.id}>
                  <TableRow
                    hover
                    sx={{
                      cursor: onViewDetails ? 'pointer' : 'default',
                      opacity: isCancelled ? 0.6 : 1,
                      backgroundColor: isCancelled ? 'error.lighter' : 'inherit',
                      '&:hover': { backgroundColor: isCancelled ? 'error.light' : undefined },
                      '& td': { py: 0.75, verticalAlign: 'top' },
                    }}
                    onClick={() => onViewDetails?.(ord)}
                  >
                    {/* Order Info: expand toggle + Order # + Created */}
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="flex-start">
                        <Tooltip title={expandedOrders.has(ord.id) ? 'Hide drugs' : 'Show drugs sent'}>
                          <IconButton
                            size="small"
                            aria-label="toggle drug detail"
                            onClick={(e) => { e.stopPropagation(); toggleExpanded(ord.id); }}
                            sx={{ p: 0.25, mt: '-2px' }}
                          >
                            {expandedOrders.has(ord.id) ? <CollapseRowIcon fontSize="small" /> : <ExpandRowIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                        <Stack spacing={0.25}>
                        {ord.orderNumber && ord.pharmacyId && ord.id ? (
                          embedded ? (
                            <Link
                              component="button"
                              variant="body2"
                              fontWeight={600}
                              fontFamily="monospace"
                              fontSize="0.75rem"
                              onClick={(e) => {
                                e.stopPropagation();
                                onOrderSelect?.(ord);
                              }}
                              sx={{
                                textDecoration: isCancelled ? 'line-through' : 'none',
                                textAlign: 'left',
                              }}
                            >
                              {ord.orderNumber}
                            </Link>
                          ) : (
                            <Link
                              component="button"
                              variant="body2"
                              fontWeight={600}
                              fontFamily="monospace"
                              fontSize="0.75rem"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/orders/${ord.pharmacyId}/${ord.id}`);
                              }}
                              sx={{
                                textDecoration: isCancelled ? 'line-through' : 'none',
                                textAlign: 'left',
                              }}
                            >
                              {ord.orderNumber}
                            </Link>
                          )
                        ) : (
                          <Typography variant="caption" fontWeight={600} fontFamily="monospace">
                            {ord.orderNumber || '-'}
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary" fontSize="0.7rem">
                          {formatDate(ord.createdAt)}
                        </Typography>
                        {ord.metadata?.billingData?.originalBillingItem?.metadata?.isOneTimeOrder && (
                          <Chip label="One Time Non-Subscription Order" size="small" variant="outlined" color="info" sx={{ fontSize: '0.6rem', height: 18 }} />
                        )}
                        {/* GP-1226: mark split halves as one linked shipment so the two
                            rows (same delivery, distinct ids/stages) are no longer read as
                            unrelated orders. Tooltip names the paired order(s) + their stage. */}
                        {splitSiblingsById.has(ord.id) && (() => {
                          const sibs = splitSiblingsById.get(ord.id)!;
                          return (
                            <Tooltip title={`Split shipment — same delivery as ${sibs.map((s) => `${s.orderNumber} (${getPrimaryStatus(s).label})`).join(', ')}. Verify and package all items together.`}>
                              <Chip
                                icon={<SplitIcon sx={{ fontSize: 12 }} />}
                                label={`Split · ${sibs.length + 1} items`}
                                size="small"
                                variant="outlined"
                                color="warning"
                                sx={{ fontSize: '0.6rem', height: 18, '& .MuiChip-icon': { ml: 0.5 } }}
                              />
                            </Tooltip>
                          );
                        })()}
                        {/* GP-1431: name the parent this row was split off, the
                            kind of split, who did it, when, and why — inline, so
                            the relationship is readable without opening both
                            order detail views and scrolling a 20-event timeline.
                            The chip opens the parent order. Rendered only from
                            RECORDED provenance (see splitProvenance.ts): a row
                            with no split record shows nothing rather than a
                            parent inferred from its order number. */}
                        {splitProvenanceById.has(ord.id) && (() => {
                          const prov = splitProvenanceById.get(ord.id)!;
                          const parentRow = prov.parentOrderNumber
                            ? orderIdsByNumber.get(prov.parentOrderNumber)
                            : undefined;
                          const parentId = prov.parentOrderId || parentRow?.id || null;
                          const actor = prov.splitByUserId
                            ? splitActorNames.get(prov.splitByUserId) || getUserDisplayName(prov.splitByUserId)
                            : null;
                          const when = formatSplitTimestamp(prov.splitAt);
                          // Embedded surfaces are "select, don't navigate" (same rule the
                          // order-number and delivery-date cells follow), so the chip is a
                          // read-only marker there.
                          const canOpenParent = !embedded && !!parentId && !!ord.pharmacyId;
                          const tooltip = [
                            `${formatSplitOrigin(prov)}${when ? ` on ${when}` : ''}${actor ? ` by ${actor}` : ''}.`,
                            prov.description,
                            prov.reason ? `Reason: ${prov.reason}` : null,
                            canOpenParent
                              ? 'Click to open the parent order.'
                              : embedded
                                ? null
                                : 'The parent order id was not recorded — search for it in All Orders.',
                          ]
                            .filter(Boolean)
                            .join(' ');
                          return (
                            <Tooltip describeChild title={tooltip}>
                              <Stack spacing={0.1} data-testid="order-split-provenance" sx={{ maxWidth: 150 }}>
                                <Chip
                                  icon={<SplitIcon sx={{ fontSize: 12 }} />}
                                  label={prov.label}
                                  size="small"
                                  variant="outlined"
                                  color="warning"
                                  onClick={
                                    canOpenParent
                                      ? (e) => {
                                          e.stopPropagation();
                                          navigate(`/orders/${ord.pharmacyId}/${parentId}`);
                                        }
                                      : undefined
                                  }
                                  sx={{
                                    fontSize: '0.6rem',
                                    height: 18,
                                    cursor: canOpenParent ? 'pointer' : 'default',
                                    '& .MuiChip-icon': { ml: 0.5 },
                                  }}
                                />
                                {/* The parent order NUMBER, wrapped — this is the line the
                                    reporter was missing. Not truncated: a half-shown order
                                    number is not an answer to "split off what?". */}
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ fontSize: '0.55rem', lineHeight: 1.2, wordBreak: 'break-all' }}
                                >
                                  from {prov.parentOrderNumber || 'another order'}
                                </Typography>
                                {(actor || when) && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ fontSize: '0.55rem', lineHeight: 1.2 }}
                                  >
                                    {[actor, when].filter(Boolean).join(' · ')}
                                  </Typography>
                                )}
                                {prov.reason && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ fontSize: '0.55rem', lineHeight: 1.2 }}
                                  >
                                    Reason: {prov.reason}
                                  </Typography>
                                )}
                              </Stack>
                            </Tooltip>
                          );
                        })()}
                        {/* GP-1391: same Rx on two unrelated orders — flag it on
                            BOTH rows instead of leaving them visually identical.
                            Error tone while either order is still in fulfilment
                            (a second dispense/charge is still preventable),
                            warning tone once both have closed. */}
                        {duplicateRxById.has(ord.id) && (() => {
                          const dup = duplicateRxById.get(ord.id)!;
                          const siblingList = dup.siblings
                            .map((s) => `${s.orderNumber} (${getPrimaryStatus(s).label})`)
                            .join(', ');
                          return (
                            <Tooltip
                              // describeChild: the chip's own label stays its
                              // accessible NAME; the long title becomes its
                              // description (a11y / getByRole).
                              describeChild
                              title={
                                dup.anyOpen
                                  ? `Duplicate prescription — Rx ${dup.rxNumbers.join(', ')} is also on ${siblingList}. Two orders for the same fill means a double dispense and a double charge. Verify in Kroll and cancel one before it ships.`
                                  : `Rx ${dup.rxNumbers.join(', ')} was also dispensed on ${siblingList}. Both orders have closed — kept visible as a record of the duplicate fill.`
                              }
                            >
                              <Chip
                                icon={<WarningIcon sx={{ fontSize: 12 }} />}
                                label={`Duplicate Rx · ${dup.siblings.length + 1} orders`}
                                size="small"
                                variant="outlined"
                                color={dup.anyOpen ? 'error' : 'warning'}
                                sx={{ fontSize: '0.6rem', height: 18, '& .MuiChip-icon': { ml: 0.5 } }}
                              />
                            </Tooltip>
                          );
                        })()}
                        {/* GP-1385: an IV-sourced order carries the NEXI visit it was
                            converted from (`nexiVisitId`, stamped by
                            POST /api/nexi/convert-to-order). Surface it on the row so the
                            order ties back to the clinic visit it came from — that linkage
                            previously existed only on the Billing Queue's Find Patient card,
                            so staff round-tripped between Patient 360 and the IV queue just to
                            recover the visit id. Clicking opens the visit in the IV Billing
                            queue's cross-date patient-events view, reusing the established
                            `?patient=<stable id>` deep link (visitId is one of the ids that
                            view matches exactly — see NexiVisitQueue / NexiBookingView).
                            Non-IV orders render nothing: no empty slot, no dangling link. */}
                        {ord.nexiVisitId && (
                          <Tooltip
                            // describeChild: keep "IV visit N" as the chip's
                            // accessible name (it can act as a button).
                            describeChild
                            title={
                              embedded
                                ? `Converted from NEXI IV visit ${ord.nexiVisitId}`
                                : `Converted from NEXI IV visit ${ord.nexiVisitId} — open it in the IV Billing queue`
                            }
                          >
                            <Chip
                              data-testid="order-nexi-visit"
                              icon={<VisitLinkIcon sx={{ fontSize: 12 }} />}
                              label={`IV visit ${ord.nexiVisitId}`}
                              size="small"
                              variant="outlined"
                              color="success"
                              // Embedded surfaces are "select, don't navigate" (same rule the
                              // order-number and delivery-date cells follow), so there the chip
                              // is a read-only marker.
                              onClick={
                                embedded
                                  ? undefined
                                  : (e) => {
                                      e.stopPropagation();
                                      navigate(
                                        `/nexi/iv-billing?patient=${encodeURIComponent(ord.nexiVisitId)}&patientName=${encodeURIComponent(ord.patientName || ord.nexiVisitId)}`,
                                      );
                                    }
                              }
                              sx={{
                                fontSize: '0.6rem',
                                height: 18,
                                cursor: embedded ? 'default' : 'pointer',
                                '& .MuiChip-icon': { ml: 0.5 },
                              }}
                            />
                          </Tooltip>
                        )}
                        </Stack>
                      </Stack>
                    </TableCell>

                    {/* Rx */}
                    <TableCell>
                      {(() => {
                        // Show only the drugs ACTIVE on this order (A4): drop split /
                        // removed / zero-fill (back-order) drugs. Owing order shows only
                        // the owed drug; the parent no longer lists it. When nothing was
                        // filtered out keep the original order-level rxNumber scalar for
                        // single-drug orders (no-regression).
                        const { rxNumbers: activeRx, removedAny } = getActiveOrderDrugs(ord);
                        const useActiveRx = activeRx.length > 1 || (removedAny && activeRx.length >= 1);
                        const rxDisplay = useActiveRx ? activeRx.join(', ') : (ord.rxNumber || '-');
                        return (
                          <Stack spacing={0.25}>
                            {/* Patient name removed — redundant inside the patient file. */}
                            <Typography variant="body2" color="text.primary" fontFamily="monospace" fontSize="0.78rem" noWrap sx={{ maxWidth: 140 }}>
                              Rx: {rxDisplay}
                            </Typography>
                            {useActiveRx && activeRx.length > 1 && (
                              <Typography variant="caption" color="primary" fontSize="0.6rem" fontWeight={500}>
                                {activeRx.length} prescriptions
                              </Typography>
                            )}
                          </Stack>
                        );
                      })()}
                    </TableCell>

                    {/* Medication / Prescriber */}
                    <TableCell>
                      {(() => {
                        // Show only the ACTIVE drugs (A4): the filtered per-drug list when
                        // available (drops split / removed / zero-fill drugs). When nothing
                        // was filtered out we keep the original order-level medicationName
                        // scalar display for single-drug orders (no-regression). When a
                        // multi-drug order was reduced to one owed drug (back-order split),
                        // show that single remaining drug's name.
                        const { medications: activeMeds, removedAny } = getActiveOrderDrugs(ord);
                        const showActiveList =
                          activeMeds && (activeMeds.length > 1 || (removedAny && activeMeds.length === 1));
                        return (
                          <Stack spacing={0.25}>
                            {showActiveList && activeMeds!.length > 1 ? (
                              <Tooltip title={activeMeds!.map((item: any) => item.medicationName || item.productName).join(', ')}>
                                <Box>
                                  {activeMeds!.map((item: any, idx: number) => (
                                    <Typography key={idx} variant="body2" fontWeight={500} fontSize="0.75rem" noWrap sx={{ maxWidth: 180 }}>
                                      {item.medicationName || item.productName || '-'}
                                    </Typography>
                                  ))}
                                </Box>
                              </Tooltip>
                            ) : showActiveList ? (
                              <Tooltip title={activeMeds![0].medicationName || activeMeds![0].productName || '-'}>
                                <Typography variant="body2" fontWeight={500} fontSize="0.8rem" noWrap sx={{ maxWidth: 180 }}>
                                  {activeMeds![0].medicationName || activeMeds![0].productName || '-'}
                                </Typography>
                              </Tooltip>
                            ) : (
                              <Tooltip title={ord.medicationName || '-'}>
                                <Typography variant="body2" fontWeight={500} fontSize="0.8rem" noWrap sx={{ maxWidth: 180 }}>
                                  {ord.medicationName || '-'}
                                </Typography>
                              </Tooltip>
                            )}
                            <Typography variant="caption" color="text.secondary" fontSize="0.7rem" noWrap sx={{ maxWidth: 180 }}>
                              {getPrescriber(ord)}
                            </Typography>
                          </Stack>
                        );
                      })()}
                    </TableCell>

                    {/* Delivery Info: Date + Method */}
                    <TableCell>
                      {(() => {
                      // GP-1338: the ETA line and the Track Delivery link describe a
                      // delivery that is actually on the books. When this cell renders a
                      // bare "-" — troubleshooting blanks the date (GP-249), or the order
                      // simply has no delivery date — there is nothing behind them, which
                      // is how a dead tracking link ended up sitting under an empty date on
                      // ORD-20260709-135539-375 (its shipment had been cancelled). Suppress
                      // both whenever no real date is shown.
                      const hasLiveDelivery = !ord.inTroubleshooting && !!(
                        ord.deliveredAt ||
                        ord.deliveryDate ||
                        ord.workflowStage === 'out_for_delivery'
                      );
                      return (
                      <Stack spacing={0.5}>
                        {/* Show delivered date if delivered, otherwise show scheduled date */}
                        {/* GP-249: Blank delivery date for troubleshooting orders */}
                        {ord.inTroubleshooting ? (
                          <Typography variant="caption" color="text.secondary">-</Typography>
                        ) : ord.deliveredAt ? (
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <DeliveredIcon sx={{ fontSize: 14, color: 'success.main' }} />
                            <Typography variant="caption" color="success.main" fontWeight={600} fontSize="0.75rem">
                              {formatDateWithDay(ord.deliveredAt)}
                            </Typography>
                          </Stack>
                        ) : ord.workflowStage === 'out_for_delivery' ? (
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <InTransitIcon sx={{ fontSize: 14, color: 'info.main' }} />
                            {ord.deliveryDate ? (
                              embedded ? (
                                <Typography variant="caption" color="info.main" fontSize="0.75rem" fontWeight={500}>
                                  {formatDateWithDay(ord.deliveryDate)}
                                </Typography>
                              ) : (
                                <Link
                                  component="button"
                                  variant="caption"
                                  color="info.main"
                                  fontSize="0.75rem"
                                  fontWeight={500}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/order-fulfillment/schedule-delivery?orderId=${encodeURIComponent(ord.id || '')}`);
                                  }}
                                >
                                  {formatDateWithDay(ord.deliveryDate)}
                                </Link>
                              )
                            ) : (
                              <Typography variant="caption" color="info.main">In Transit</Typography>
                            )}
                          </Stack>
                        ) : (
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <ScheduleIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                            {ord.deliveryDate ? (
                              embedded ? (
                                <Typography variant="caption" color="primary" fontSize="0.75rem" fontWeight={500}>
                                  {formatDateWithDay(ord.deliveryDate)}
                                </Typography>
                              ) : (
                                <Link
                                  component="button"
                                  variant="caption"
                                  color="primary"
                                  fontSize="0.75rem"
                                  fontWeight={500}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/order-fulfillment/schedule-delivery?orderId=${encodeURIComponent(ord.id || '')}`);
                                  }}
                                >
                                  {formatDateWithDay(ord.deliveryDate)}
                                </Link>
                              )
                            ) : (
                              <Typography variant="caption" color="text.secondary">-</Typography>
                            )}
                          </Stack>
                        )}

                        {/* Delivery method */}
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <DeliveryIcon sx={{ fontSize: 12, color: 'action.disabled' }} />
                          <Typography variant="caption" color="text.secondary" fontSize="0.65rem">
                            {ord.deliveryMethod || '-'}
                          </Typography>
                        </Stack>
                        {/* GP-99: Delivery address */}
                        {formatAddress(ord) && (
                          <Typography variant="caption" color="text.secondary" fontSize="0.6rem" noWrap sx={{ maxWidth: 180 }}>
                            {formatAddress(ord)}
                          </Typography>
                        )}
                        {/* GP-27: ETA — only while a delivery is actually on the books (GP-1338) */}
                        {hasLiveDelivery && ord.descartesPlannedArrivalTime && (
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <EtaIcon sx={{ fontSize: 12, color: 'info.main' }} />
                            <Typography variant="caption" color="info.main" fontSize="0.65rem" fontWeight={500}>
                              ETA: {ord.descartesPlannedArrivalTime}
                            </Typography>
                          </Stack>
                        )}
                        {/* GP-27: Tracking Link — prefer the backend-resolved URL
                            (descartesTrackingUrl, from the canonical descartes_deliveries
                            table) so bag-scan orders whose descartesTrackId column is null
                            still get a link; fall back to the id-built URL. Mirrors the
                            OrderDetail.tsx gate. GP-1338 adds the hasLiveDelivery gate so
                            a cancelled/blanked delivery no longer offers a dead link. */}
                        {hasLiveDelivery && (ord.descartesTrackingUrl || ord.descartesTrackId) && (
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <TrackingLinkIcon sx={{ fontSize: 11, color: 'primary.main' }} />
                            <Link
                              href={ord.descartesTrackingUrl || `https://ondemand.descartes.com/nkshealth/OrderStatus/track?id=${ord.descartesTrackId}`}
                              target="_blank"
                              rel="noopener"
                              variant="caption"
                              fontSize="0.6rem"
                              onClick={(e) => e.stopPropagation()}
                            >
                              Track Delivery
                            </Link>
                          </Stack>
                        )}
                        {/* Courier (ShipStation) tracking link — Purolator / FedEx /
                            Canada Post etc. Descartes and ShipStation are mutually
                            exclusive per order, so this only renders when there is no
                            Descartes link. Reuses the shared getTrackingUrl carrier-URL
                            builder; falls back to the bare number when the carrier is
                            unknown (so the tracking # is never hidden). */}
                        {hasLiveDelivery && !ord.descartesTrackingUrl && !ord.descartesTrackId && ord.shipstationTrackingNumber && (() => {
                          const url = getTrackingUrl(ord.shipstationCarrierCode, ord.shipstationTrackingNumber);
                          return (
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <TrackingLinkIcon sx={{ fontSize: 11, color: 'primary.main' }} />
                              {url ? (
                                <Link
                                  href={url}
                                  target="_blank"
                                  rel="noopener"
                                  variant="caption"
                                  fontSize="0.6rem"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  Track Delivery
                                </Link>
                              ) : (
                                <Typography variant="caption" color="text.secondary" fontSize="0.6rem">
                                  {ord.shipstationTrackingNumber}
                                </Typography>
                              )}
                            </Stack>
                          );
                        })()}
                      </Stack>
                      );
                      })()}
                    </TableCell>

                    {/* Status / Stage — one meaningful pill (outcome for terminal
                        orders, current stage for active ones) + priority marker. */}
                    <TableCell>
                      <Stack spacing={0.25} alignItems="flex-start">
                        {(() => {
                          const p = getPrimaryStatus(ord);
                          const pill = (
                            <Chip
                              label={p.label}
                              color={p.color}
                              variant={p.variant}
                              size="small"
                              sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600, cursor: p.clickable ? 'pointer' : 'default' }}
                            />
                          );
                          if (!p.clickable) {
                            return p.tooltip ? (
                              <Tooltip describeChild title={p.tooltip}>
                                {pill}
                              </Tooltip>
                            ) : (
                              pill
                            );
                          }
                          if (embedded) {
                            return (
                              <Tooltip title={ord.inTroubleshooting && ord.troubleshootingReason ? `T/S Reason: ${ord.troubleshootingReason}` : ''}>
                                {pill}
                              </Tooltip>
                            );
                          }
                          return (
                            <Tooltip title={ord.inTroubleshooting && ord.troubleshootingReason ? `T/S Reason: ${ord.troubleshootingReason}` : 'Click to go to queue'}>
                              <Link
                                component="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const route = getStageRoute(ord.workflowStage, ord);
                                  // GP-1379: queue search boxes match patient name /
                                  // order number / RX — never the internal UUID. Sending
                                  // only `orderId` landed the operator on an empty queue
                                  // pre-filtered by a GUID that matches nothing. Send the
                                  // order NUMBER as well; `orderId` stays for the queues
                                  // that still read it.
                                  const params = new URLSearchParams();
                                  if (ord.orderNumber) params.set('orderNumber', ord.orderNumber);
                                  params.set('orderId', ord.id || '');
                                  if (ord.inTroubleshooting) {
                                    params.set('tab', 'troubleshooting');
                                  } else if (ord.workflowStage === 'clinic_scheduling') {
                                    params.set('tab', 'troubleshooting');
                                  } else if (route === DELIVERY_STATUS_ROUTE) {
                                    // GP-1379: the Delivery Status queue splits pickups
                                    // and deliveries into tabs that exclude each other, so
                                    // the landing tab has to follow the order's own
                                    // fulfilment method. Unknown method → no hint, and the
                                    // queue keeps its own default (never a guess).
                                    const tab = deliveryStatusTabFor(ord);
                                    if (tab) params.set('tab', tab);
                                  }
                                  navigate(`${route}?${params.toString()}`);
                                }}
                                sx={{ textDecoration: 'none' }}
                              >
                                {pill}
                              </Link>
                            </Tooltip>
                          );
                        })()}

                        {/* GP-1338: reconcile this row with the troubleshooting queues.
                            An order whose `completed` status was overtaken by a later
                            stage says so explicitly (the pill above already shows the
                            live stage); one that genuinely delivered but still carries
                            an open troubleshooting flag gets a marker, because the
                            Scheduling / Billing troubleshooting tabs list it as open
                            work off that flag alone and never look at `status`. */}
                        {(() => {
                          const { isReopenedAfterCompletion, hasOpenTroubleshooting } =
                            deriveOrderCompletionState(ord);
                          if (isReopenedAfterCompletion) {
                            return (
                              <Tooltip describeChild title={`This order was marked Completed but has since re-entered the workflow at ${getStageDisplayName(ord.workflowStage, ord)}${ord.troubleshootingReason ? ` — ${ord.troubleshootingReason}` : ''}.`}>
                                <Typography
                                  variant="caption"
                                  sx={{ fontSize: '0.55rem', fontWeight: 700, lineHeight: 1.1, color: 'warning.dark' }}
                                >
                                  ↩ Reopened after completion
                                </Typography>
                              </Tooltip>
                            );
                          }
                          if (hasOpenTroubleshooting) {
                            return (
                              <Tooltip describeChild title={`Still an open troubleshooting item${ord.troubleshootingReason ? ` — ${ord.troubleshootingReason}` : ''}.`}>
                                <Typography
                                  variant="caption"
                                  sx={{ fontSize: '0.55rem', fontWeight: 700, lineHeight: 1.1, color: 'warning.dark' }}
                                >
                                  ⚑ Open troubleshooting
                                </Typography>
                              </Tooltip>
                            );
                          }
                          return null;
                        })()}

                        {/* Priority — only while the order is active and elevated */}
                        {ord.priority && isActiveOrder(ord) && ['critical', 'urgent', 'high'].includes(String(ord.priority).toLowerCase()) && (
                          <Typography
                            variant="caption"
                            sx={{ fontSize: '0.55rem', fontWeight: 700, lineHeight: 1, color: `${getPriorityColor(ord.priority)}.main` }}
                          >
                            ▲ {String(ord.priority).toUpperCase()}
                          </Typography>
                        )}

                        {/* GP-1226: combined/blocking status for split shipments. When a
                            paired item on the SAME delivery is still being fulfilled at a
                            DIFFERENT stage (the exact "one cleared Ready, the other stranded
                            in Packaging" gap), warn here instead of showing two independent
                            stage badges — so the verifier handles the whole basket together. */}
                        {(() => {
                          const sibs = splitSiblingsById.get(ord.id);
                          if (!sibs || sibs.length === 0) return null;
                          const lagging = sibs.filter(
                            (s) => isActiveOrder(s) && s.workflowStage && s.workflowStage !== ord.workflowStage,
                          );
                          if (lagging.length === 0) return null;
                          const stages = Array.from(
                            new Set(lagging.map((s) => getStageDisplayName(s.workflowStage, s))),
                          );
                          return (
                            <Tooltip title={`This delivery has ${sibs.length + 1} items. Paired item still in ${stages.join(', ')} — verify and package all items together before completing.`}>
                              <Stack direction="row" spacing={0.25} alignItems="center" sx={{ color: 'warning.main', maxWidth: 150 }}>
                                <WarningIcon sx={{ fontSize: 12 }} />
                                <Typography variant="caption" sx={{ fontSize: '0.55rem', fontWeight: 700, lineHeight: 1.1 }}>
                                  Paired item in {stages.join(', ')}
                                </Typography>
                              </Stack>
                            </Tooltip>
                          );
                        })()}
                      </Stack>
                    </TableCell>

                    {/* Payment */}
                    <TableCell align="center">
                      <Stack spacing={0.25} alignItems="center">
                        <Typography variant="caption" fontWeight={500} fontSize="0.75rem">
                          {ord.copayAmount != null && !isNaN(Number(ord.copayAmount))
                            ? `$${Number(ord.copayAmount).toFixed(2)}`
                            : '-'}
                        </Typography>
                        <Chip
                          label={ord.paymentStatus === 'paid' || ord.paymentCollectedAt ? 'Paid' : 'Pending'}
                          color={ord.paymentStatus === 'paid' || ord.paymentCollectedAt ? 'success' : 'default'}
                          size="small"
                          sx={{ height: 16, fontSize: '0.55rem' }}
                        />
                      </Stack>
                    </TableCell>

                    {/* Verification triad — Data / Clinical / Product completion */}
                    <TableCell align="center">
                      {getOrderRxNumbers(ord).length === 0 ? (
                        <Typography variant="caption" color="text.disabled">-</Typography>
                      ) : (
                        <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center">
                          {getVerificationSummary(ord).map((c) => (
                            <Tooltip
                              key={c.type}
                              describeChild
                              title={
                                c.missingAfterDispatch
                                  ? `${c.label} verification MISSING — this order is ${formatWorkflowStage(ord.workflowStage)} with ${c.count}/${c.total} Rx signed. It left the pharmacy without this check.`
                                  : c.state === 'none'
                                    ? `${c.label} verification: none`
                                    : `${c.label} verification: ${c.count}/${c.total}${c.signers.length ? ` — ${c.signers.join(', ')}` : ''}`
                              }
                            >
                              <Box
                                data-testid={
                                  c.missingAfterDispatch ? 'order-check-missing-after-dispatch' : undefined
                                }
                                sx={{
                                  minWidth: 18,
                                  height: 18,
                                  px: c.state === 'partial' || c.missingAfterDispatch ? 0.4 : 0,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: 0.25,
                                  borderRadius: '4px',
                                  border: '1px solid',
                                  borderColor: c.missingAfterDispatch
                                    ? 'error.main'
                                    : c.state === 'complete'
                                      ? 'success.main'
                                      : c.state === 'partial'
                                        ? 'warning.main'
                                        : 'divider',
                                  // GP-1436: a check missing on an order that already
                                  // shipped is filled solid red — never the same faint
                                  // grey as "not its turn yet".
                                  bgcolor: c.missingAfterDispatch
                                    ? 'error.main'
                                    : c.state === 'complete'
                                      ? 'success.main'
                                      : 'transparent',
                                  color:
                                    c.missingAfterDispatch || c.state === 'complete'
                                      ? 'common.white'
                                      : c.state === 'partial'
                                        ? 'warning.dark'
                                        : 'text.disabled',
                                  fontSize: '0.6rem',
                                  fontWeight: 700,
                                  lineHeight: 1,
                                }}
                              >
                                {c.short}
                                {c.missingAfterDispatch ? (
                                  <Box component="span" sx={{ fontSize: '0.5rem', fontWeight: 700 }}>
                                    {c.state === 'none' ? '!' : `${c.count}/${c.total}`}
                                  </Box>
                                ) : (
                                  c.state === 'partial' && (
                                    <Box component="span" sx={{ fontSize: '0.5rem', fontWeight: 600 }}>
                                      {c.count}/{c.total}
                                    </Box>
                                  )
                                )}
                              </Box>
                            </Tooltip>
                          ))}
                        </Stack>
                      )}
                    </TableCell>

                    {/* Actions */}
                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      <Stack direction="row" spacing={0} justifyContent="center">
                        {ord.pharmacyId && ord.id && (
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (embedded) {
                                  onOrderSelect?.(ord);
                                } else {
                                  navigate(`/orders/${ord.pharmacyId}/${ord.id}`);
                                }
                              }}
                              sx={{ p: 0.5 }}
                            >
                              <ViewIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {/* GP-1222: create a counseling task straight from the
                            order (patient / Rx / prescriber already in context) —
                            no need to re-enter everything on the Task Queue. */}
                        {onAddCounselingTask && (
                          <Tooltip title="Add Counseling Task">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                onAddCounselingTask(ord);
                              }}
                              sx={{ p: 0.5 }}
                              color="primary"
                            >
                              <CounselIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Print">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedOrderForPrint(ord);
                              setPrintModalOpen(true);
                            }}
                            sx={{ p: 0.5 }}
                          >
                            <PrintIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                        {/* Show Swap RX button only for completed/delivered orders */}
                        {(ord.workflowStage === 'delivered' ||
                          ord.workflowStage === 'picked_up' ||
                          ord.status === 'completed') && (
                          <Tooltip title="Swap RX #">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedOrderForSwap(ord);
                                setSwapRxDialogOpen(true);
                              }}
                              sx={{ p: 0.5 }}
                              color="warning"
                            >
                              <SwapIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {onTrackOrder && ord.workflowStage && !embedded && (
                          <Tooltip title="Track">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                onTrackOrder(ord);
                              }}
                              sx={{ p: 0.5 }}
                            >
                              <TrackIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>

                  {/* Expandable per-drug detail: name / strength / qty / days / next follow-up */}
                  <TableRow>
                    <TableCell colSpan={8} sx={{ py: 0, border: 0, bgcolor: 'grey.50' }}>
                      <Collapse in={expandedOrders.has(ord.id)} timeout="auto" unmountOnExit>
                        <Box sx={{ p: 1.5 }}>
                          <OrderVerificationPhotosRow pharmacyId={ord.pharmacyId || ''} orderId={ord.id || ''} orderNumber={ord.orderNumber}>
                              {(photos: VerificationPhoto[]) => {
                            const drugs = getOrderDrugDetails(ord);
                            const sentLabel = ord.deliveredAt
                              ? `Sent ${formatDateWithDay(ord.deliveredAt)}`
                              : ord.deliveryDate
                                ? `Scheduled ${formatDateWithDay(ord.deliveryDate)}`
                                : 'Not yet sent';
                            // PRX-68: badge a drug's Product check when it has its own
                            // item photo, or the order carries a pack photo (covers
                            // every Rx on the order).
                            const hasVerificationPhoto = (rxNumber: string): boolean =>
                              photos.some((p) => p.scope === 'pack' || String(p.rxNumber ?? '') === rxNumber);
                            if (drugs.length === 0) {
                              return <Typography variant="caption" color="text.secondary">No drug detail on this order.</Typography>;
                            }
                            return (
                              <>
                                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                  {drugs.length} drug{drugs.length > 1 ? 's' : ''} · {sentLabel}
                                </Typography>
                                <Table size="small" sx={{ '& td, & th': { borderBottom: '1px solid', borderColor: 'divider', py: 0.5 } }}>
                                  <TableHead>
                                    <TableRow sx={{ '& th': { fontSize: '0.65rem', fontWeight: 700, color: 'text.secondary' } }}>
                                      <TableCell>Drug</TableCell>
                                      <TableCell>Strength</TableCell>
                                      <TableCell align="right">Qty</TableCell>
                                      <TableCell align="right">Days</TableCell>
                                      <TableCell>Next follow-up</TableCell>
                                      <TableCell align="center">Checks</TableCell>
                                      <TableCell align="center">Print</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {drugs.map((d) => (
                                      <TableRow key={d.rxNumber || d.name}>
                                        <TableCell>
                                          <Stack direction="row" spacing={0.5} alignItems="center">
                                            <Typography variant="body2" fontSize="0.75rem" fontWeight={500}>{d.name}</Typography>
                                            {d.isPrn && (
                                              <Tooltip title="PRN (as needed) — no scheduled refill. Edit the subscription to manage it.">
                                                <Chip
                                                  label="PRN"
                                                  size="small"
                                                  variant="outlined"
                                                  color="info"
                                                  sx={{ height: 16, fontSize: '0.55rem', fontWeight: 700 }}
                                                />
                                              </Tooltip>
                                            )}
                                          </Stack>
                                          {d.rxNumber && (
                                            <Typography variant="caption" color="text.secondary" fontFamily="monospace" fontSize="0.6rem">Rx {d.rxNumber}</Typography>
                                          )}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: '0.75rem' }}>{d.strength || '—'}</TableCell>
                                        <TableCell align="right" sx={{ fontSize: '0.75rem' }}>{d.quantity != null ? Number(d.quantity) : '—'}</TableCell>
                                        <TableCell align="right" sx={{ fontSize: '0.75rem' }}>{d.daysSupply != null ? d.daysSupply : '—'}</TableCell>
                                        <TableCell sx={{ fontSize: '0.75rem' }}>
                                          {d.nextFollowUp ? (
                                            formatDateWithDay(d.nextFollowUp)
                                          ) : d.isPrn ? (
                                            <Typography component="span" variant="caption" sx={{ color: 'info.main', fontStyle: 'italic', fontSize: '0.7rem' }}>
                                              PRN · as needed
                                            </Typography>
                                          ) : (
                                            <Typography component="span" variant="caption" color="text.disabled" fontSize="0.7rem">—</Typography>
                                          )}
                                        </TableCell>
                                        <TableCell align="center">
                                          <Stack direction="row" spacing={0.5} justifyContent="center">
                                            {d.checks.map((c) => {
                                              // PRX-68: a small camera badge on the Product check
                                              // when this Rx has its own item photo, or the order
                                              // carries a pack photo (covers every Rx on it).
                                              const showPhotoBadge = c.short === 'P' && hasVerificationPhoto(d.rxNumber);
                                              return (
                                              <Tooltip key={c.short} title={showPhotoBadge ? `${c.label}: ${c.done ? 'verified' : 'not verified'} — product verification photo on file` : `${c.label}: ${c.done ? 'verified' : 'not verified'}`}>
                                                <Box
                                                  sx={{
                                                    width: 16,
                                                    height: 16,
                                                    borderRadius: '4px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: '0.55rem',
                                                    fontWeight: 700,
                                                    border: '1px solid',
                                                    borderColor: c.done ? 'success.main' : 'divider',
                                                    bgcolor: c.done ? 'success.main' : 'transparent',
                                                    color: c.done ? 'common.white' : 'text.disabled',
                                                    position: 'relative',
                                                  }}
                                                >
                                                  {c.short}
                                                  {showPhotoBadge && (
                                                    <CameraAltIcon
                                                      aria-label="Product verification photo on file"
                                                      sx={{
                                                        position: 'absolute',
                                                        top: -6,
                                                        right: -6,
                                                        fontSize: 10,
                                                        bgcolor: 'background.paper',
                                                        borderRadius: '50%',
                                                        color: 'primary.main',
                                                      }}
                                                    />
                                                  )}
                                                </Box>
                                              </Tooltip>
                                              );
                                            })}
                                          </Stack>
                                        </TableCell>
                                        <TableCell align="center">
                                          <Tooltip title="Print this drug's label">
                                            <IconButton size="small" onClick={() => handlePrintDrug(ord, d)} sx={{ p: 0.25 }}>
                                              <PrintIcon sx={{ fontSize: 15 }} />
                                            </IconButton>
                                          </Tooltip>
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </>
                            );
                              }}
                          </OrderVerificationPhotosRow>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={filteredAndSortedData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          sx={{ '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: '0.75rem' } }}
        />
      </TableContainer>

      {/* Label Printing Modal */}
      {selectedOrderForPrint && (
        <LabelPrintingModal
          open={printModalOpen}
          onClose={() => {
            setPrintModalOpen(false);
            setSelectedOrderForPrint(null);
          }}
          orderId={selectedOrderForPrint.id}
          orderNumber={selectedOrderForPrint.orderNumber}
          patientName={selectedOrderForPrint.patientName}
          medications={withReprintParity(selectedOrderForPrint, selectedOrderForPrint.medications || (() => {
            // Use order items if available (multi-Rx orders have individual items)
            if (selectedOrderForPrint.items && selectedOrderForPrint.items.length > 0) {
              return selectedOrderForPrint.items.map((item: any) => ({
                rxNumber: item.rxNumber || '',
                medication: item.medicationName || item.productName || '',
                strength: item.strength || '',
                quantity: item.quantity ? String(item.quantity) : '',
                directions: '',
                prescriber: '',
                din: item.productDin || '',
                upc: item.productUpc || '',
              }));
            }

            // Fallback: parse from order-level fields (comma-separated rxNumber, pipe-separated medicationName)
            const rxNumberStr = selectedOrderForPrint.rxNumber || '';
            const medicationName = selectedOrderForPrint.medicationName || '';
            const rxNumbers = rxNumberStr.split(',').map((rx: string) => rx.trim()).filter((rx: string) => rx);
            const medicationNames = medicationName.split('|').map((m: string) => m.trim());

            if (rxNumbers.length === 0) {
              return [{
                rxNumber: '',
                medication: medicationName,
                strength: '',
                quantity: '',
                directions: '',
                prescriber: ''
              }];
            }

            return rxNumbers.map((rx: string, index: number) => ({
              rxNumber: rx,
              medication: medicationNames[index] || medicationName || '',
              strength: '',
              quantity: '',
              directions: '',
              prescriber: ''
            }));
          })())}
          deliveryAddress={(() => {
            // GP-415: Use the order's frozen deliveryAddressSnapshot so the
            // delivery label matches the address the order was created with,
            // not the patient's first/default address from Kroll.
            // GP-564 (reopen): also pass through the unit/suite number so the
            // reprinted label from the Patient 360 Order Table matches what
            // the Packaging Queue prints — the original GP-564 fix wired
            // unit through PackagingVerification but missed this code path.
            const snap = selectedOrderForPrint.deliveryAddressSnapshot;
            if (snap && (snap.street || snap.city)) {
              return {
                street: snap.street || '',
                unit: snap.unit || '',
                city: snap.city || '',
                province: snap.province || '',
                postalCode: snap.postalCode || snap.postal_code || '',
                // GP-887: snapshots on this path carry mixed casing (see postalCode above)
                deliveryInstructions: snap.delivery_instructions || snap.deliveryInstructions || undefined,
              };
            }
            return undefined;
          })()}
          // GP-1234: same field the Delivery column above renders, so the printed
          // label and the grid can never disagree on pickup vs delivery.
          deliveryMethod={selectedOrderForPrint.deliveryMethod}
          deliveryType={selectedOrderForPrint.deliveryType}
          shipmentDate={selectedOrderForPrint.deliveryDate || selectedOrderForPrint.deliveryDateOverride}
          // GP-1008/GP-1322: the lot/expiry captured at packaging and the original
          // product verifier now ride on each MEDICATION (see withReprintParity
          // above) rather than as one order-level value, because both are per-Rx
          // facts. Deliberately NOT passed as an order-level default here — doing
          // so would print the FIRST Rx's lot/expiry on a different drug's label
          // when that drug's own packaging record is missing, the same
          // cross-Rx-contamination bug this consolidation removes from the
          // resolver itself (see getPackagedLotExpiryForRx).
          // GP-521/GP-664/GP-1317: reprint parity for the nursing-home name — a tag
          // reprinted from Patient 360 must sort by home like the packaging one.
          // Order payloads on this page carry the Kroll group stamp under
          // `metadata.krollGroup` (queue/detail shape) or, on the delivery-status
          // shape, promoted to the top level; read both, default neither.
          nhGroupName={
            selectedOrderForPrint.metadata?.krollGroup?.groupName ??
            selectedOrderForPrint.krollGroup?.groupName
          }
        />
      )}

      {/* Swap RX Dialog */}
      {selectedOrderForSwap && pharmacyId && (
        <SwapRxDialog
          open={swapRxDialogOpen}
          onClose={() => {
            setSwapRxDialogOpen(false);
            setSelectedOrderForSwap(null);
          }}
          order={selectedOrderForSwap}
          pharmacyId={pharmacyId}
          onSuccess={() => {
            setSwapRxDialogOpen(false);
            setSelectedOrderForSwap(null);
            onRefresh?.();
          }}
        />
      )}

      {/* Past Orders Dialog */}
      {patientId && patientName && pharmacyId && (
        <PastOrdersDialog
          open={orderHistoryOpen}
          onClose={() => setOrderHistoryOpen(false)}
          patientId={patientId}
          patientName={patientName}
          pharmacyId={pharmacyId}
        />
      )}
    </Box>
  );
};
